import { useContext, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { STEPS, DISABLED_STEPS } from "./constants";
import { AppContext } from "../../../scripts/context/App";
import axios from "axios";
import toast from "react-hot-toast";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 700,
  minHeight: 400,
  bgcolor: "background.paper",
  borderRadius: "4px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
};

interface IToogleModal {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setIsEnabled: (isEnabled: boolean) => void;
  isEnabled: boolean;
  data: {
    google2fa_code: string;
    google2fa_qrcode: string;
  };
}

function ToggleModal({
  data,
  isOpen,
  setIsOpen,
  setIsEnabled,
  isEnabled,
}: IToogleModal) {
  const [activeStep, setActiveStep] = useState(0);
  const [code, setCode] = useState("");

  const handleClose = () => {
    setIsOpen(false);

    setActiveStep(0);
  };

  const handleNext = () => {
    if (activeStep === getSteps.length - 1) {
      if (!code) {
        return toast.error("Make sure you enter a code and try again.");
      } else if (code.length !== 6) {
        return toast.error("Make sure you enter a valid code and try again.");
      }
      if (isEnabled) {
        axios
          .post("/2fa/disable", {
            one_time_password: code,
          })
          .then(() => {
            setIsOpen(false);
            setActiveStep(0);
            setIsEnabled(!isEnabled);
          })
          .catch(({ response }) => {
            toast.error(response.data.message);
          });
      } else {
        axios
          .post("/2fa/enable", {
            one_time_password: code,
            google2fa_secret: data.google2fa_code,
          })
          .then(() => {
            setIsOpen(false);
            setActiveStep(0);
            setIsEnabled(!isEnabled);
          })
          .catch(({ response }) => {
            toast.error(response.data.message);
          });
      }

      setCode("");
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getSteps = isEnabled ? DISABLED_STEPS : STEPS;

  const StepComponent = getSteps[activeStep].component;

  return (
    <Modal
      keepMounted
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="google-authentication-code"
    >
      <Box sx={style}>
        <Stepper
          activeStep={activeStep}
          sx={{
            p: { xs: 1, sm: 3 },
            borderBottom: "1px solid rgba(72, 94, 144, 0.16)",
          }}
        >
          {getSteps.map((step, index) => (
            <Step key={index}>
              <StepLabel>
                <Typography
                  sx={{
                    fontSize: "11px",
                    display: {
                      xs: activeStep === index ? "block" : "none",
                      sm: "block",
                    },
                  }}
                  variant="body2"
                  color="textPrimary"
                >
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ flex: 1, p: 2 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              {getSteps[activeStep].label}
            </Typography>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                fontSize:
                  getSteps[activeStep].label === "Backup Key" ? "13px" : "1rem",
              }}
            >
              {getSteps[activeStep].description}
            </Typography>
          </Box>

          {/* must center the content */}
          <StepComponent data={data} code={code} setCode={setCode} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            p: 1,
            borderTop: "1px solid rgba(72, 94, 144, 0.16)",
          }}
        >
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>

          <Box sx={{ flex: "1 1 auto" }} />

          <Button onClick={handleNext}>
            {activeStep === getSteps.length - 1 ? "Finish" : "Next"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default function ToggleTwoFactorAuthentication() {
  const { user, setState } = useContext(AppContext);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authenticationData, setAuthenticationData] = useState({
    google2fa_code: "",
    google2fa_qrcode: "",
  });

  const getAuthenticationData = async () => {
    if (user.two_fa) return setOpenModal(true);

    setLoading(true);
    try {
      const { data } = await axios.get("/2fa/get-data");
      setAuthenticationData(data.data);

      setOpenModal(true);
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response.data.message || "Something went wrong. Please try again."
      )
    }

    setLoading(false);
  };

  return (
    <>
      <ToggleModal
        data={authenticationData}
        isEnabled={user.two_fa}
        setIsEnabled={(boolean: Boolean) =>
          setState({ user: { ...user, two_fa: boolean } })
        }
        isOpen={openModal}
        setIsOpen={setOpenModal}
      />

      <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 4, pt: 3 }}>
        <Typography variant="h6" color="textPrimary" sx={{ mb: 1 }}>
          Two Factor Authentication
        </Typography>
        <Box
          sx={{
            maxWidth: 500,
            display: "flex",
            flexWrap: "wrap",
            boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            gap: 2,
            p: 2,
          }}
        >
          <img
            src={
              require("../../../assets/ToggleTwoFactorAuthenticationIcon.svg")
                .default
            }
            alt="ToggleTwoFactorAuthenticationIcon"
            style={{ width: "40px" }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 2,
              flex: "1 1 345px",
            }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "500",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "3px",
                }}
              >
                Google Authentication is
                {user.two_fa ? (
                  <Typography
                    sx={{ color: "success.main", fontWeight: "bold" }}
                  >
                    Enabled
                  </Typography>
                ) : (
                  <Typography sx={{ color: "error.main", fontWeight: "bold" }}>
                    Disabled
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enable two factor authentication to add an extra layer of
                security to your account.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              color="primary"
              onClick={getAuthenticationData}
              disabled={loading}
              startIcon={
                loading && (
                  <CircularProgress
                    sx={{ width: "15px !important", height: "15px !important" }}
                  />
                )
              }
            >
              {user.two_fa ? "OFF" : "ON"}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
